import React from 'react';
import {
    SelectHierarchy,
    Select,
    ToggleContainer,
    Section,
    FrameCard,
    Input,
    Form,
    Message,
    Tooltip,
    TooltipHelp,
    Button,
    Icon,
    PreviewCard,
    ToastContext
} from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { LANGUAGES } from '../../../../components/translations/translationModal';
import UploadFileZone from '../../../../components/upload/uploadFileZone';
import LoadingCard from '../../../../components/loadingCard';
import Style from '../designForm.module.css';
import { typesKeyNames } from '../../list/components/helper';

import DesignModel from '../../../../models/designModel';
import { templateTypes } from '../../../../models/designTemplateModel';
import { stylizeEventOption } from '../../../../components/events/select-events/utils';

export default function PreviewForm({
    isTemplate = false,
    organization,
    event = null,
    events = [],
    rates = [],
    loadingEvents = false,
    loadingRates = false,
    form,
    formErrors = null,
    setFormErrors,
    setForm,
    setFormObject,
    dataPreview,
    canDownload = true,
    updateValidationError = () => {}
}) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [togglePreview, setTogglePreview] = React.useState(false);

    const selectListEvents = React.useMemo(() => {
        if (events?.length > 0)
            return events
                .filter(e => e.publication_status !== 'closed')
                .map(e => {
                    return {
                        value: e.id,
                        key: e.id,
                        text: stylizeEventOption(e)
                    };
                });
        return [];
    }, [events]);

    const languageOptions = React.useMemo(() => {
        const listLanguages = event?.languages || organization.languages;
        return listLanguages.map(l => {
            return {
                value: l,
                key: l,
                text: LANGUAGES[l]
            };
        });
    }, [organization, event]);

    const isAlpha = str => {
        if (!str || str.length === 0) return true;
        const alphaRegex = new RegExp(/[A-Za-z0-9]+/gm);

        const matches = str.match(alphaRegex);
        return matches?.length === 1 && matches[0] === str;
    };

    const onChange = React.useCallback(
        (select, key) => {
            if (key === 'event_id') setForm('rate_id', null);
            if (key === 'barcode') setFormErrors(null);
            setForm(key, select);
        },
        [setForm, setFormErrors]
    );

    const downloadPreview = React.useCallback(() => {
        DesignModel.perform_preview({ data: { ...dataPreview, is_download: true } }, { organization, event })
            .then(_src => {
                if (isTemplate) updateValidationError(null);
                window.open(_src.response.preview);
            })
            .catch(_error => {
                if (_error.response) {
                    if (isTemplate) {
                        //we only need to update the twig errors during template creation/edition
                        updateValidationError(_error.response);
                        setFormErrors(_error.response);
                    }
                    const errorObject = JSON.parse(_error.response);
                    if (errorObject && errorObject['barcode']) setFormErrors(errorObject);
                }
                toast.error(t('ticket.ticket_design.create_design.preview.error'));
            });
    }, [dataPreview, organization, event, isTemplate]);

    return (
        <FrameCard title={t('ticket.common.action.preview')} subtitle={t('ticket.ticket_design.create_design.preview.subtitle')}>
            {isTemplate && [templateTypes.PDF_TICKET, templateTypes.PDF_BADGE].includes(dataPreview?.type) && form && (
                <>
                    <Message type="default" title={t('ticket.ticket_design.create_design.preview.background')}>
                        {t('ticket.ticket_design.create_design.preview.temporary')}
                    </Message>
                    {t('ticket.ticket_design.create_design.preview.file')}
                    <UploadFileZone
                        subLabelDropzone={`(${t('nacre.UploadButton.sub-label-dropzone')})`}
                        maxSize={2097152}
                        file={form?.background}
                        organization={organization}
                        fileTypes={['.pdf']}
                        onUpload={url => setForm('background', url.url)}
                        onDelete={() => setForm('background', null)}
                    />
                </>
            )}
            <Form.Field>
                <Form.Label>{t('ticket.common.language')}</Form.Label>
                <Select disabled={togglePreview} useNew value={form['language']} options={languageOptions} onChange={selected => onChange(selected, 'language')} />
            </Form.Field>
            <ToggleContainer
                toggle={togglePreview}
                onToggle={checked => {
                    setFormObject(
                        {
                            rate_id: null,
                            attendee_id: null,
                            event_id: checked && event ? event.id : null
                        },
                        true
                    );
                    setTogglePreview(checked);
                }}
                title={t('ticket.ticket_design.create_design.preview.event_or_attendee')}
            >
                {form && (
                    <Section>
                        {!event && (
                            <LoadingCard loading={loadingEvents}>
                                <Form.Field>
                                    <Form.Label optionalText={t('ticket.common.labelOptional')}>{t('ticket.common.event')}</Form.Label>
                                    <Select
                                        useNew
                                        disabled={Boolean(event)}
                                        value={form['event_id']}
                                        options={selectListEvents}
                                        placeholder={t('ticket.ticket_design.create_design.preview.select_event')}
                                        onChange={selected => {
                                            onChange(selected, 'event_id');
                                            onChange(events.find(e => e.id === selected)?.default_language, 'language');
                                        }}
                                        onClear={() => onChange(null, 'event_id')}
                                    />
                                </Form.Field>
                            </LoadingCard>
                        )}
                        <Form.Field>
                            <Form.Label optionalText={t('ticket.common.labelOptional')}>{t('ticket.common.rate')}</Form.Label>
                            <LoadingCard loading={loadingRates && Boolean(form?.event_id)}>
                                <SelectHierarchy
                                    disabled={!dataPreview?.event_id}
                                    widthIndent="10px"
                                    value={form?.rate_id}
                                    options={rates}
                                    placeholder={t('ticket.ticket_design.create_design.preview.select_rate')}
                                    onChange={selected => onChange(selected, 'rate_id')}
                                    onClear={() => onChange(null, 'rate_id')}
                                />
                            </LoadingCard>
                        </Form.Field>
                        <Form.Field>
                            <Form.Label optionalText={t('ticket.common.labelOptional')}>
                                {t('ticket.ticket_design.create_design.preview.barcode')}
                                <TooltipHelp title={t('ticket.ticket_design.create_design.preview.barcode_help')} />
                            </Form.Label>
                            <Input value={form?.barcode || ''} placeholder="Ex: 1234567" onChange={e => isAlpha(e.target.value) && onChange(e.target.value, 'barcode')} />
                            <Form.FieldErrors errors={[formErrors && t('ticket.ticket_design.create_design.preview.barcode_error')]} />
                        </Form.Field>
                    </Section>
                )}
            </ToggleContainer>
            {/* Download only available for PDF types */}
            <Tooltip
                title={t('ticket.ticket_design.create_design.preview.no_download')}
                triggerOnHover
                visible={![templateTypes.PDF_TICKET, templateTypes.PDF_BADGE].includes(dataPreview?.type)}
            >
                <Button
                    disabled={![templateTypes.PDF_TICKET, templateTypes.PDF_BADGE].includes(dataPreview?.type) || !canDownload}
                    color="ticket"
                    onClick={() => downloadPreview()}
                >
                    <Icon name="download" /> {t('ticket.ticket_design.create_design.preview.download')}
                </Button>
            </Tooltip>
        </FrameCard>
    );
}

export function PreviewImage({ disabled, previewSrc, loadingPreview, reloadPreview, templateType, previewValid }) {
    const { t } = useTranslation();

    return (
        <PreviewCard className={Style['design-preview-card']} title={<>{t('ticket.common.action.preview')}</>}>
            <LoadingCard loading={loadingPreview}>
                {!previewValid && (
                    <Message type="danger" title={t('ticket.ticket_design.create_design.preview.unavailable-message.title')}>
                        {t('ticket.ticket_design.create_design.preview.unavailable-message.body')}
                    </Message>
                )}
                {previewSrc && !disabled && templateType && (
                    <div className={`${Style['preview-img-container']}`}>
                        {' '}
                        <img className={`${Style['preview-img']} ${Style[`preview-img-${typesKeyNames[templateType]}`]}`} src={previewSrc} alt="preview ticket" />{' '}
                    </div>
                )}
            </LoadingCard>
            <Button disabled={disabled} primary inverted className={Style['card-footer-button']} onClick={() => reloadPreview()}>
                <Icon name="Reset" /> {t('ticket.ticket_design.create_design.preview.refresh')}
            </Button>
        </PreviewCard>
    );
}
